/**
 * Preview-only port of `App\Classes\SpaceManager\Pricing\TieredPriceCalculator`.
 *
 * Keep in sync with that class, but never move authoritative pricing here — the price stored on an
 * allocation is always the one the server calculated on save. This exists so the admin sees the
 * effect of changing times or weekdays before submitting.
 */
import { PriceTier, SubscriptionTemplatePriceBasis } from '../types';

const MINUTES_PER_DAY = 24 * 60;

/**
 * The server evaluates the tiers in `app.timezone_display`, never in the timezone of whoever is
 * clicking. Reading the wall clock the same way keeps the preview from drifting by the offset for
 * an admin whose machine is not on Berlin time.
 */
const DISPLAY_TIMEZONE = 'Europe/Berlin';

const DISPLAY_CLOCK = new Intl.DateTimeFormat('en-GB', {
    timeZone: DISPLAY_TIMEZONE,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
});

/** 0 = Monday … 6 = Sunday, matching FormInput.Weekdays and the backend. */
const WEEKDAY_INDEX: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

/** Wall clock and weekday of a moment as the backend sees them. */
function displayClock(date: Date): { minutes: number; weekday: number } {
    const parts = DISPLAY_CLOCK.formatToParts(date);
    const part = (type: string) => parts.find((entry) => entry.type === type)?.value ?? '';

    // some engines render midnight as "24" under hour12: false
    const hours = Number(part('hour')) % 24;

    return {
        minutes: hours * 60 + Number(part('minute')),
        weekday: WEEKDAY_INDEX[part('weekday')] ?? 0
    };
}

/** "HH:mm" to minutes since midnight; null when the value is missing or malformed. */
function toMinutes(value?: string | null): number | null {
    if (!value) return null;

    const [hours, minutes] = value.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

    return hours * 60 + minutes;
}

/**
 * Sum of all tier prices a booking of the given times on the given weekday would trigger.
 *
 * @param weekday 0 = Monday … 6 = Sunday, matching FormInput.Weekdays and the backend
 */
export function calculateTieredPrice(
    tiers: PriceTier[],
    start: Date,
    end: Date,
    weekday: number,
    basis: SubscriptionTemplatePriceBasis
): number {
    const allocationStart = displayClock(start).minutes;
    let allocationEnd = displayClock(end).minutes;

    // a booking running past midnight ends on the next day
    if (allocationEnd <= allocationStart) allocationEnd += MINUTES_PER_DAY;

    return tiers.reduce((total, tier) => {
        // an empty weekday list means "every day"
        if (Array.isArray(tier.weekdays) && tier.weekdays.length > 0 && !tier.weekdays.includes(weekday)) {
            return total;
        }

        const tierStart = toMinutes(tier.start);
        let tierEnd = toMinutes(tier.end);
        if (tierStart === null || tierEnd === null) return total;

        // overnight tiers such as 22:00-02:00
        if (tierEnd <= tierStart) tierEnd += MINUTES_PER_DAY;

        const overlap = Math.min(allocationEnd, tierEnd) - Math.max(allocationStart, tierStart);
        if (overlap <= 0) return total;

        const price = Number(tier.price) || 0;

        return total + (basis === 'per_hour' ? price * (overlap / 60) : price);
    }, 0);
}

/**
 * The distinct per-date totals across the selected weekdays, ascending.
 * An empty weekday selection means the series runs on every day.
 */
export function tieredPriceRange(
    tiers: PriceTier[],
    start: Date,
    end: Date,
    weekdays: number[] | undefined,
    basis: SubscriptionTemplatePriceBasis
): number[] {
    const days = weekdays?.length ? weekdays : [0, 1, 2, 3, 4, 5, 6];
    const totals = days.map((day) => calculateTieredPrice(tiers, start, end, day, basis));

    return Array.from(new Set(totals.map((total) => Math.round(total * 100) / 100))).sort((a, b) => a - b);
}

/**
 * How many dates a series would produce, mirroring the loop in AllocationController::store().
 *
 * Only needed to preview a `total` basis, which divides by that count. An estimate is good enough
 * here — the binding number is the one the server counts when the dates actually exist.
 */
export function estimateOccurrences(
    start: Date,
    repeatUntil: Date,
    repeatPeriodInDays: number,
    weekdays: number[] | undefined,
    exceptions: { start?: string | null; end?: string | null }[] | undefined
): number {
    const period = Number(repeatPeriodInDays);
    if (!period || period < 1 || !start || !repeatUntil) return 1;

    const days = weekdays?.length ? weekdays : [0, 1, 2, 3, 4, 5, 6];
    const ranges = (exceptions ?? [])
        .filter((exception) => exception.start && exception.end)
        .map((exception) => [
            new Date(exception.start as string).setHours(0, 0, 0, 0),
            new Date(exception.end as string).setHours(23, 59, 59, 999)
        ]);

    const cursor = new Date(start);
    const until = new Date(repeatUntil);
    let count = 0;
    // the same generous bound the backend implicitly has via repeat_until
    let guard = 0;

    while (cursor <= until && guard < 2000) {
        guard += 1;

        const weekday = displayClock(cursor).weekday;
        const time = cursor.getTime();
        const excluded = ranges.some(([from, to]) => time >= from && time <= to);

        if (days.includes(weekday) && !excluded) count += 1;

        cursor.setDate(cursor.getDate() + period);
    }

    return Math.max(1, count);
}
